/**
 * TwainBridgeStatus — colored-dot health indicator for the PCBTwainBridge
 * local daemon. Rendered next to the "Scan ID" button so officers get
 * instant visual feedback about whether the on-prem scanner service is up.
 *
 * See `useTwainBridge.ts` for the underlying health probe + source discovery.
 * Brand tokens (var(--pcb-*) — see pcb-brand skill):
 *   - ready   → green  (#386732)
 *   - missing → yellow (#f3de8a)
 *   - error   → red    (#b10505)
 *   - checking→ gray   (#8e99a4)
 */

import type { JSX } from 'react';
import type { TwainBridgeStatus as TwainBridgeStatusType } from '../../hooks/useTwainBridge.js';

interface Props {
  status: TwainBridgeStatusType;
}

interface StatusVisuals {
  color: string;
  label: string;
  bg: string;
}

function visualsFor(s: TwainBridgeStatusType): StatusVisuals {
  switch (s.status) {
    case 'ready': {
      const src = s.selectedSource ?? (s.sources && s.sources[0]) ?? 'default';
      return { color: 'var(--ok-text)', bg: 'var(--ok-bg)', label: `Scanner ready (${src})` };
    }
    case 'missing':
      return {
        color: 'var(--warn-text)',
        bg: 'var(--warn-bg)',
        label: s.errorMessage ?? 'Scanner unavailable — use Camera or Upload',
      };
    case 'error':
      return { color: 'var(--err-text)', bg: 'var(--err-bg)', label: s.errorMessage ?? 'Scanner error' };
    case 'checking':
    default:
      return { color: 'var(--ink-600)', bg: 'var(--ink-200)', label: 'Checking scanner…' };
  }
}

export function TwainBridgeStatus({ status }: Props): JSX.Element {
  const v = visualsFor(status);
  const titleParts = [
    `status: ${status.status}`,
    status.version ? `bridge v${status.version}` : null,
    status.sources && status.sources.length > 0 ? `sources: ${status.sources.join(', ')}` : null,
  ].filter(Boolean);
  return (
    <span
      role="status"
      aria-live="polite"
      title={titleParts.join(' · ')}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        padding: '4px 10px',
        borderRadius: 999,
        background: v.bg,
        color: v.color,
        fontSize: 12,
        fontWeight: 600,
        lineHeight: 1.2,
        fontFamily: "'Segoe UI', Roboto, sans-serif",
      }}
    >
      <span
        aria-hidden="true"
        style={{
          display: 'inline-block',
          width: 8,
          height: 8,
          borderRadius: '50%',
          background: v.color,
        }}
      />
      {v.label}
    </span>
  );
}
